/**
 * BL Academy — the buyer and BL advisor classroom.
 *
 * Short YouTube lessons the super admin curates, arranged along the acquisition
 * learning path. The page loads in one request (GET /academy/overview) because
 * it renders the hero, the journey rail, the catalog and the right rail at
 * once — four requests would have meant four staggered paints.
 *
 * Every counter on the page (momentum ring, stage progress, saved actions, the
 * resume target) is derived from the lessons in that payload by the helpers in
 * lib/academy.ts. That is deliberate: the numbers have to change the instant
 * someone saves or completes something, and a server-computed copy would be a
 * second implementation of the same rules.
 */
import { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Bookmark,
  BookmarkCheck,
  Check,
  CheckCircle2,
  Clock,
  Compass,
  FileSignature,
  FolderOpen,
  GraduationCap,
  KeyRound,
  LayoutGrid,
  type LucideIcon,
  Play,
  PlayCircle,
  Route,
  Search,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { EmptyPanel, PageHero } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  continueLesson as pickContinueLesson,
  currentStage as pickCurrentStage,
  formatDuration,
  formatInvested,
  lessonCategories,
  momentum as computeMomentum,
  savedActions as computeSavedActions,
  stageProgress as computeStageProgress,
  CARD_TONES,
  STAGE_ICONS,
  youtubeThumbnail,
  type AcademyLesson,
  type AcademyOverview,
  type AcademyProgressState,
  type AcademyStageProgress,
} from "@/lib/academy";

import LessonDialog from "./LessonDialog";

type View = "overview" | "library" | "saved" | "toolkit";

const VIEWS: { key: View; label: string; icon: LucideIcon }[] = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "library", label: "All lessons", icon: PlayCircle },
  { key: "saved", label: "Saved lessons", icon: Bookmark },
  { key: "toolkit", label: "My buyer toolkit", icon: FolderOpen },
];

/** The hero blurb per tab. The heading itself is fixed — see PageHero below. */
const VIEW_DESCRIPTION: Record<View, string> = {
  overview: "Build the knowledge to find, evaluate, and buy the right business.",
  library: "Explore every step of buying a business, a few minutes at a time.",
  saved: "Pick up the lessons you want to revisit.",
  toolkit: "Keep your acquisition criteria, questions, and next steps together.",
};

/** Resolves the icon name STAGE_ICONS declares. Unknown stages get the compass. */
const ICON_BY_NAME: Record<string, LucideIcon> = {
  Compass,
  Search,
  BarChart3,
  FileSignature,
  KeyRound,
};

function stageIcon(stageKey: string): LucideIcon {
  return ICON_BY_NAME[STAGE_ICONS[stageKey]] ?? Compass;
}

export default function AcademyPage() {
  const toast = useToast();
  const [data, setData] = useState<AcademyOverview | null>(null);
  const [failed, setFailed] = useState(false);

  const [view, setView] = useState<View>("overview");
  const [category, setCategory] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [openLessonId, setOpenLessonId] = useState<string | null>(null);
  const [pathOpen, setPathOpen] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);

  useEffect(() => {
    api
      .get<AcademyOverview>("/academy/overview")
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  // Memoized rather than `data?.lessons ?? []`: that allocates a fresh array on
  // every render, which would change the identity every derived useMemo below
  // depends on and recompute all of them on every keystroke in the search box.
  const lessons = useMemo(() => data?.lessons ?? [], [data]);

  /** Fold a progress change from the dialog back into the loaded lessons. */
  const applyProgress = (lessonId: string, progress: AcademyProgressState) => {
    setData((prior) =>
      prior
        ? {
            ...prior,
            lessons: prior.lessons.map((l) => (l.id === lessonId ? { ...l, progress } : l)),
          }
        : prior,
    );
  };

  const stages = useMemo(
    () => computeStageProgress(data?.stages ?? [], lessons),
    [data?.stages, lessons],
  );
  const here = useMemo(() => pickCurrentStage(stages), [stages]);
  const stats = useMemo(() => computeMomentum(lessons), [lessons]);
  const resume = useMemo(() => pickContinueLesson(lessons), [lessons]);
  const actions = useMemo(() => computeSavedActions(lessons), [lessons]);
  const categories = useMemo(() => lessonCategories(lessons), [lessons]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return lessons.filter((l) => {
      if (view === "saved" && !l.progress?.is_saved) return false;
      if (stageFilter && l.stage !== stageFilter) return false;
      if (category && l.category !== category) return false;
      if (needle) {
        const haystack = `${l.title} ${l.category ?? ""} ${l.summary ?? ""}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [lessons, view, stageFilter, category, query]);

  const openLesson = lessons.find((l) => l.id === openLessonId) ?? null;
  // What this viewer can actually open. A toolkit card or a case study may
  // point at a lesson that is still a draft, archived, or published to another
  // role — the admin picker offers every lesson regardless of status — and
  // without this check those buttons would look live and do nothing at all.
  const openableIds = useMemo(() => new Set(lessons.map((l) => l.id)), [lessons]);

  /** Switching views clears the filters, so a stale search can't hide content. */
  const changeView = (next: View) => {
    setView(next);
    setCategory(null);
    setStageFilter(null);
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const jumpToStage = (stageKey: string) => {
    setView("library");
    setCategory(null);
    setQuery("");
    setStageFilter(stageKey);
    setPathOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleSaved = async (lesson: AcademyLesson) => {
    const next = !lesson.progress?.is_saved;
    try {
      const updated = await api.patch<AcademyProgressState>(
        `/academy/lessons/${lesson.id}/progress`,
        { is_saved: next },
      );
      applyProgress(lesson.id, updated);
      toast.success(next ? "Lesson added to your saved list." : "Lesson removed from your saved list.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that lesson.");
    }
  };

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* ── Page heading ──
            The shared Dark · Gold interior hero, same component and same
            "BL <accent>" shape as Mastermind, AI Deal Matching and the rest —
            this page used to hand-roll its own header, which made it the only
            interior page with a different one.

            The title stays fixed; the description carries the current view, so
            the hero still says which of the four tabs you are on. */}
        <div className="mb-8">
          <PageHero
            eyebrow="Know more. Buy with confidence."
            icon={GraduationCap}
            title="BL"
            titleAccent="Academy"
            description={VIEW_DESCRIPTION[view]}
            actions={
              // The hero-action convention: the Button component, size lg,
              // rounded-full. `variant="outline"` and nothing else — the hero
              // shell remaps the palette onto itself, so the variant's own
              // bg-card/text-foreground resolve to the dark values and read
              // correctly on the gold hero in both themes. (Some older pages
              // pin `bg-white/80` here, which lands light-on-light.)
              <Button
                variant="outline"
                size="lg"
                className="rounded-full"
                onClick={() => setPathOpen(true)}
              >
                <Route className="h-4 w-4" />
                My learning path
              </Button>
            }
          />
        </div>

        {/* ── View switcher ── */}
        <nav className="mt-6 flex flex-wrap gap-1.5 border-b border-border pb-px">
          {VIEWS.map((item) => {
            const Icon = item.icon;
            const active = view === item.key;
            const count =
              item.key === "saved" ? stats.saved : item.key === "library" ? stats.total : null;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => changeView(item.key)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "-mb-px inline-flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm font-semibold transition-colors",
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
                {count !== null && count > 0 && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[0.62rem] font-bold tabular-nums text-muted-foreground">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {failed ? (
          <div className="mt-8">
            <EmptyPanel
              icon={TriangleAlert}
              title="Couldn't load BL Academy"
              description="Something went wrong fetching your lessons. Please refresh the page to try again."
            />
          </div>
        ) : data === null ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <div className="mt-7 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_19rem]">
            {/* ── Main column ── */}
            <div className="min-w-0 space-y-8">
              {view === "overview" && (
                <>
                  <ContinueCard
                    lesson={resume}
                    catalogEmpty={stats.total === 0}
                    onOpen={setOpenLessonId}
                  />
                  <JourneySection
                    stages={stages}
                    here={here}
                    onPick={jumpToStage}
                    onViewPath={() => setPathOpen(true)}
                  />
                </>
              )}

              {view === "toolkit" ? (
                <ToolkitSection
                  items={data.toolkit_items}
                  actions={actions}
                  openableIds={openableIds}
                  onOpen={setOpenLessonId}
                />
              ) : (
                <CatalogSection
                  view={view}
                  lessons={visible}
                  categories={categories}
                  category={category}
                  onCategory={setCategory}
                  query={query}
                  onQuery={setQuery}
                  stageFilter={stages.find((s) => s.key === stageFilter) ?? null}
                  onClearStage={() => setStageFilter(null)}
                  onOpen={setOpenLessonId}
                  onToggleSaved={toggleSaved}
                  onBrowseAll={() => changeView("library")}
                />
              )}
            </div>

            {/* ── Right rail ── */}
            <aside className="space-y-5">
              <MomentumPanel stats={stats} here={here} />
              {data.case_study && (
                <CaseCard caseStudy={data.case_study} onOpen={() => setCaseOpen(true)} />
              )}
              <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <FolderOpen className="h-5 w-5 text-primary" />
                <h3 className="heading-bold mt-3 font-display text-[1.2rem] text-foreground">Learn it. Apply it.</h3>
                <p className="mt-1.5 text-xs leading-6 text-muted-foreground">
                  Turn an insight into your next seller question.
                </p>
                <button
                  type="button"
                  onClick={() => changeView("toolkit")}
                  className="mt-3 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
                >
                  Open your buyer toolkit →
                </button>
              </div>
              <div className="flex gap-3 rounded-2xl border border-dashed border-border bg-muted/20 p-4">
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                <span className="text-[0.7rem] leading-6 text-muted-foreground">
                  Practical education.
                  <br />
                  Your advisors guide your deal.
                </span>
              </div>
            </aside>
          </div>
        )}
      </div>

      {openLesson && (
        <LessonDialog
          key={openLesson.id}
          lesson={openLesson}
          onClose={() => setOpenLessonId(null)}
          onProgressChange={applyProgress}
        />
      )}

      {pathOpen && (
        <PathDialog stages={stages} here={here} onPick={jumpToStage} onClose={() => setPathOpen(false)} />
      )}

      {caseOpen && data?.case_study && (
        <CaseDialog
          caseStudy={data.case_study}
          openableIds={openableIds}
          onClose={() => setCaseOpen(false)}
          onOpenLesson={(id) => {
            setCaseOpen(false);
            setOpenLessonId(id);
          }}
        />
      )}
    </div>
  );
}

// ── Continue learning ────────────────────────────────────────────────
/**
 * The hero card. Always dark regardless of theme: it sits on top of the video's
 * own thumbnail, so its text colours are pinned rather than tokenised — a
 * theme-flipping foreground would land dark-on-dark over the image.
 */
function ContinueCard({
  lesson,
  catalogEmpty,
  onOpen,
}: {
  lesson: AcademyLesson | null;
  /** No lessons published to this viewer at all — a different thing entirely
      from having finished the ones there are. */
  catalogEmpty: boolean;
  onOpen: (id: string) => void;
}) {
  if (!lesson) {
    // Both states land here, and congratulating someone on completing a course
    // that does not exist yet reads as a broken page.
    return (
      <section className="rounded-[1.5rem] border border-border bg-card p-8 text-center shadow-card">
        {catalogEmpty ? (
          <>
            <GraduationCap className="mx-auto h-9 w-9 text-muted-foreground/60" />
            <h2 className="heading-bold mt-3 font-display text-[1.875rem] text-foreground">
              Lessons are on their way.
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-muted-foreground">
              Nothing has been published to BL Academy yet. This is where your next lesson will
              appear.
            </p>
          </>
        ) : (
          <>
            <CheckCircle2 className="mx-auto h-9 w-9 text-primary" />
            <h2 className="heading-bold mt-3 font-display text-[1.875rem] text-foreground">You're all caught up.</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-muted-foreground">
              Every lesson available to you is complete. New lessons appear here as they're
              published.
            </p>
          </>
        )}
      </section>
    );
  }

  const watched = lesson.progress?.last_position_seconds ?? 0;
  const pct =
    lesson.duration_seconds > 0 ? Math.min(100, (watched / lesson.duration_seconds) * 100) : 0;
  // Published lessons always carry a video id (the API refuses to publish
  // without one), so this only has to satisfy the type, not a real case.
  // No video yet means no thumbnail. The scrim is opaque enough to carry the
  // copy on its own, so the card loses its photograph rather than rendering a
  // broken <img> — src="" would still hit the network and show a torn icon.
  const thumb =
    lesson.thumbnail_url ||
    (lesson.youtube_video_id ? youtubeThumbnail(lesson.youtube_video_id) : null);

  return (
    <section className="relative overflow-hidden rounded-[1.5rem] border border-border/60 bg-[#141009] shadow-card">
      {thumb && (
        <img src={thumb} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
      )}
      {/* Pinned dark scrim so the copy stays readable over any thumbnail. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(100deg, rgba(20,16,10,0.96) 0%, rgba(20,16,10,0.9) 42%, rgba(20,16,10,0.45) 72%, rgba(20,16,10,0.25) 100%)",
        }}
      />
      <div className="relative z-10 max-w-xl p-7 sm:p-9">
        <span className="inline-flex items-center gap-2 text-[0.62rem] font-bold uppercase tracking-[0.18em] text-primary-light">
          <span className="h-1.5 w-1.5 rounded-full bg-primary-light" />
          {watched > 0 ? "Continue learning" : "Start here"}
        </span>
        {lesson.category && (
          <div className="mt-3 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-white/55">
            {lesson.category}
          </div>
        )}
        <h2 className="heading-bold mt-2 font-display text-[1.875rem] leading-tight text-white sm:text-[2.5rem]">
          {lesson.title}
        </h2>
        {lesson.summary && (
          <p className="mt-3 max-w-md text-sm leading-7 text-white/70">{lesson.summary}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-white/60">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {formatDuration(lesson.duration_seconds)}
          </span>
          <span>{lesson.stage_label}</span>
        </div>
        <button
          type="button"
          onClick={() => onOpen(lesson.id)}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-[filter] hover:brightness-[1.06]"
        >
          <PlayCircle className="h-4 w-4" />
          {watched > 0 ? "Continue lesson" : "Start lesson"} →
        </button>

        {watched > 0 && lesson.duration_seconds > 0 && (
          <div className="mt-5 max-w-sm">
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/15">
              <div className="h-full rounded-full bg-primary-light" style={{ width: `${pct}%` }} />
            </div>
            <small className="mt-2 block text-[0.68rem] text-white/55">
              Pick up where you left off · {formatDuration(watched)} /{" "}
              {formatDuration(lesson.duration_seconds)}
            </small>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Acquisition journey ──────────────────────────────────────────────
function JourneySection({
  stages,
  here,
  onPick,
  onViewPath,
}: {
  stages: AcademyStageProgress[];
  here: AcademyStageProgress | null;
  onPick: (key: string) => void;
  onViewPath: () => void;
}) {
  if (stages.length === 0) return null;
  return (
    <section>
      <SectionHead
        title="Your acquisition journey"
        description="A clear path from your first question to your first acquisition."
        action={
          <button
            type="button"
            onClick={onViewPath}
            className="shrink-0 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
          >
            View path →
          </button>
        }
      />
      {/* One panel holding the whole path, with the stages joined by a
          continuous rule — the steps read as a single journey rather than as
          five separate cards. Scrolls sideways on a phone instead of wrapping,
          so the sequence is never broken across lines. */}
      <div className="overflow-x-auto rounded-2xl border border-border bg-card px-3 py-6 shadow-card">
        <div className="flex min-w-[36rem] items-start">
          {stages.map((stage, i) => {
            const Icon = stageIcon(stage.key);
            const done = stage.lessonCount > 0 && stage.completedCount === stage.lessonCount;
            const current = here?.key === stage.key;
            return (
              <div key={stage.key} className="flex min-w-0 flex-1 items-start">
                <button
                  type="button"
                  onClick={() => onPick(stage.key)}
                  className="group flex min-w-0 flex-1 flex-col items-center gap-3 px-1 text-center"
                >
                  <span
                    className={cn(
                      "flex h-12 w-12 items-center justify-center rounded-full transition-colors",
                      current
                        ? "bg-gold-gradient text-primary-foreground shadow-sm"
                        : done
                          ? "bg-primary/15 text-primary"
                          : "bg-muted text-muted-foreground group-hover:bg-accent",
                    )}
                  >
                    {done ? <Check className="h-5 w-5" /> : <Icon className="h-[1.15rem] w-[1.15rem]" />}
                  </span>
                  <strong className="px-1 text-[0.82rem] font-bold leading-5 text-foreground">
                    {stage.journey_title}
                  </strong>
                  <small
                    className={cn(
                      "-mt-1 text-[0.72rem]",
                      current ? "font-semibold text-primary" : "text-muted-foreground",
                    )}
                  >
                    {current
                      ? "You are here"
                      : stage.lessonCount === 0
                        ? "Coming soon"
                        : `${stage.lessonCount} short lesson${stage.lessonCount === 1 ? "" : "s"}`}
                  </small>
                </button>
                {/* The connector, aligned to the middle of the 12-unit circle.
                    Not rendered after the last stage, which would leave a rule
                    running off the end of the path. */}
                {i < stages.length - 1 && (
                  <span aria-hidden className="mt-6 h-px w-full min-w-3 flex-1 bg-border" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Lesson catalog ───────────────────────────────────────────────────
function CatalogSection({
  view,
  lessons,
  categories,
  category,
  onCategory,
  query,
  onQuery,
  stageFilter,
  onClearStage,
  onOpen,
  onToggleSaved,
  onBrowseAll,
}: {
  view: View;
  lessons: AcademyLesson[];
  categories: string[];
  category: string | null;
  onCategory: (c: string | null) => void;
  query: string;
  onQuery: (q: string) => void;
  stageFilter: AcademyStageProgress | null;
  onClearStage: () => void;
  onOpen: (id: string) => void;
  onToggleSaved: (lesson: AcademyLesson) => void;
  onBrowseAll: () => void;
}) {
  return (
    <section>
      <SectionHead
        title={
          view === "saved"
            ? "Your saved lessons"
            : view === "overview"
              ? "A little knowledge. A real advantage."
              : "The lesson library"
        }
        description={
          view === "saved"
            ? "A personal shortlist for the decisions ahead."
            : "Practical lessons for the decisions ahead of you."
        }
        action={
          view === "overview" ? (
            <button
              type="button"
              onClick={onBrowseAll}
              className="shrink-0 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
            >
              Explore all lessons →
            </button>
          ) : undefined
        }
      />

      {/* Topic tabs + search. The tabs are built from the categories the
          published lessons actually use, so there is never an empty tab. */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap gap-1.5" role="tablist" aria-label="Lesson topic">
          <TopicTab active={category === null} onClick={() => onCategory(null)}>
            {view === "saved" ? "All saved" : "For you"}
          </TopicTab>
          {categories.map((c) => (
            <TopicTab key={c} active={category === c} onClick={() => onCategory(c)}>
              {c}
            </TopicTab>
          ))}
        </div>
        <label className="flex shrink-0 items-center gap-2 rounded-xl border border-input bg-background px-3 py-2 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search lessons"
            aria-label="Search lessons"
            className="w-full min-w-0 bg-transparent text-xs outline-none sm:w-40"
          />
        </label>
      </div>

      {stageFilter && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{stageFilter.label}</span>
          <span>· {lessons.length} lessons</span>
          <button
            type="button"
            onClick={onClearStage}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 font-semibold text-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            Clear filter <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {lessons.length === 0 ? (
        <EmptyPanel
          icon={view === "saved" ? Bookmark : GraduationCap}
          title={view === "saved" ? "No saved lessons yet." : "No lessons here yet."}
          description={
            view === "saved"
              ? "Bookmark a lesson and it will be waiting for you here."
              : "Try another topic, clear your search, or check back once more lessons are published."
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {lessons.map((lesson, i) => (
            <LessonCard
              key={lesson.id}
              lesson={lesson}
              tone={lesson.card_tone || CARD_TONES[i % CARD_TONES.length]}
              onOpen={() => onOpen(lesson.id)}
              onToggleSaved={() => onToggleSaved(lesson)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * A lesson card: the typographic tile from the approved design.
 *
 * Deliberately NOT the video's thumbnail. The tile carries a short statement
 * set large — the hook — with the lesson's actual title printed underneath it,
 * so the grid reads as a set of ideas rather than a row of video stills. The
 * three colourways alternate down the grid (see `.academy-tile-*` in
 * index.css, which themes each one for light and dark).
 */
function LessonCard({
  lesson,
  tone,
  onOpen,
  onToggleSaved,
}: {
  lesson: AcademyLesson;
  tone: string;
  onOpen: () => void;
  onToggleSaved: () => void;
}) {
  const completed = !!lesson.progress?.is_completed;
  const saved = !!lesson.progress?.is_saved;
  // The headline is the hook; the title is the label. With no headline written
  // the title does both jobs rather than leaving the tile blank.
  const headline = lesson.card_headline?.trim() || lesson.title;

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40 hover:shadow-card">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${lesson.title}`}
        className={cn(
          "group relative flex w-full flex-col justify-between p-5 text-left",
          `academy-tile-${tone}`,
        )}
        style={{ minHeight: "13.5rem" }}
      >
        <div className="flex w-full items-start justify-between gap-3">
          <span className="academy-tile-muted text-[0.6rem] font-bold uppercase tracking-[0.14em]">
            {lesson.category || "BL Academy"}
          </span>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-current opacity-70 transition-all duration-200 group-hover:scale-110 group-hover:opacity-100">
            <Play className="ml-0.5 h-3 w-3 fill-current" />
          </span>
        </div>

        {/* The hook. Clamped rather than allowed to run past the tile: a long
            headline would otherwise overflow the artwork, which is exactly how
            the original mockup broke at this size. */}
        <p
          className="my-4 overflow-hidden font-display text-[1.875rem] font-bold leading-[1.15] tracking-[-0.01em]"
          style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4 }}
        >
          {headline}
        </p>

        <div className="flex w-full items-end justify-between gap-3">
          <span className="academy-tile-muted text-[0.58rem] font-bold uppercase tracking-[0.16em]">
            BL Academy
          </span>
          <span className="rounded-md bg-black/70 px-1.5 py-0.5 text-[0.65rem] font-semibold tabular-nums text-white">
            {formatDuration(lesson.duration_seconds)}
          </span>
        </div>
      </button>

      <div className="flex flex-1 flex-col p-4">
        {lesson.category && (
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-primary">
            {lesson.category}
          </div>
        )}
        <button
          type="button"
          onClick={onOpen}
          className="mt-1 text-left text-sm font-bold leading-6 text-foreground transition-colors hover:text-primary"
        >
          {lesson.title}
        </button>
        <div className="flex-1" />
        <div className="mt-3.5 flex items-center justify-between gap-2 border-t border-border/60 pt-3">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-[0.68rem]",
              completed ? "font-semibold text-emerald-600" : "text-muted-foreground",
            )}
          >
            {completed ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" />
                Completed
              </>
            ) : (
              "One lesson. One next step."
            )}
          </span>
          <button
            type="button"
            onClick={onToggleSaved}
            aria-pressed={saved}
            aria-label={`${saved ? "Remove" : "Save"} ${lesson.title}`}
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
              saved ? "text-primary" : "text-muted-foreground hover:text-primary",
            )}
          >
            {saved ? (
              <BookmarkCheck className="h-4 w-4 fill-current" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

// ── Buyer toolkit ────────────────────────────────────────────────────
function ToolkitSection({
  items,
  actions,
  openableIds,
  onOpen,
}: {
  items: AcademyOverview["toolkit_items"];
  actions: { lesson: AcademyLesson; note: string }[];
  openableIds: Set<string>;
  onOpen: (id: string) => void;
}) {
  return (
    <section>
      <SectionHead
        title="Put your learning to work."
        description="Your questions, criteria, and next steps, all in one place."
      />

      {items.length === 0 ? (
        <EmptyPanel
          icon={FolderOpen}
          title="No worksheets yet."
          description="Guided worksheets appear here once they're published. Your saved actions from any lesson show up below."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {items.map((item) => (
            <article key={item.id} className="rounded-2xl border border-border bg-card p-5 shadow-card">
              <FolderOpen className="h-5 w-5 text-primary" />
              <h3 className="heading-bold mt-3 font-display text-[1.2rem] text-foreground">{item.title}</h3>
              {item.description && (
                <p className="mt-1.5 text-xs leading-6 text-muted-foreground">{item.description}</p>
              )}
              {item.lesson_id && openableIds.has(item.lesson_id) ? (
                <button
                  type="button"
                  onClick={() => onOpen(item.lesson_id!)}
                  className="mt-3 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
                >
                  Open guided worksheet →
                </button>
              ) : (
                // Either the lesson was deleted, or it is not published to this
                // viewer yet. Say so rather than rendering a live-looking button
                // that opens nothing.
                <p className="mt-3 text-[0.68rem] italic text-muted-foreground">
                  Lesson coming soon.
                </p>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="mt-8">
        <SectionHead
          title="Your saved actions"
          description="The next steps you wrote down as you worked through each lesson."
        />
        {actions.length === 0 ? (
          <EmptyPanel
            icon={Sparkles}
            title="Nothing saved yet."
            description="Open any lesson and write your next step to start your personal toolkit."
          />
        ) : (
          <div className="space-y-3">
            {actions.map(({ lesson, note }) => (
              <div key={lesson.id} className="rounded-2xl border border-border bg-card p-4 shadow-card">
                <button
                  type="button"
                  onClick={() => onOpen(lesson.id)}
                  className="text-left text-sm font-bold text-foreground transition-colors hover:text-primary"
                >
                  {lesson.title}
                </button>
                <p className="mt-1.5 whitespace-pre-wrap text-xs leading-6 text-muted-foreground">
                  {note}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Right rail ───────────────────────────────────────────────────────
function MomentumPanel({
  stats,
  here,
}: {
  stats: { total: number; completed: number; saved: number; invested: number };
  here: AcademyStageProgress | null;
}) {
  // r=42 -> circumference 2πr ≈ 264. The dash array is "filled gap".
  const CIRCUMFERENCE = 264;
  const ratio = stats.total > 0 ? Math.min(stats.completed / stats.total, 1) : 0;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <h3 className="heading-bold font-display text-[1.2rem] text-foreground">Your momentum</h3>
        <BarChart3 className="h-4 w-4 text-primary" />
      </div>

      <div className="relative mx-auto mt-4 h-32 w-32">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" strokeWidth="7" className="stroke-border" />
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            strokeWidth="7"
            strokeLinecap="round"
            className="stroke-primary transition-[stroke-dasharray] duration-500"
            strokeDasharray={`${ratio * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <strong className="font-display text-[1.875rem] leading-none tabular-nums text-foreground">
            {stats.completed}
            <span className="text-[1.15rem] text-muted-foreground">/{stats.total}</span>
          </strong>
          <small className="mt-0.5 text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            Lessons
          </small>
        </div>
      </div>

      <p className="mt-3 text-center text-xs leading-6 text-muted-foreground">
        {stats.completed === 0
          ? "Start with one lesson. It compounds."
          : stats.completed === stats.total
            ? "Every lesson complete. Well done."
            : "You're building a strong foundation."}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border/60 pt-4">
        <div>
          <strong className="block font-display text-[1.6rem] leading-none tabular-nums text-foreground">
            {stats.completed}
          </strong>
          <small className="text-[0.66rem] text-muted-foreground">Lessons completed</small>
        </div>
        <div>
          <strong className="block font-display text-[1.6rem] leading-none tabular-nums text-foreground">
            {formatInvested(stats.invested)}
          </strong>
          <small className="text-[0.66rem] text-muted-foreground">Time invested</small>
        </div>
      </div>

      {here && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-border/70 bg-muted/30 p-3">
          <Route className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <strong className="block text-xs font-bold text-foreground">Current stage</strong>
            <small className="block truncate text-[0.68rem] text-muted-foreground">
              {here.label}
            </small>
          </div>
        </div>
      )}
    </div>
  );
}

function CaseCard({
  caseStudy,
  onOpen,
}: {
  caseStudy: NonNullable<AcademyOverview["case_study"]>;
  onOpen: () => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-primary">
        {caseStudy.eyebrow}
      </div>
      {caseStudy.badge && (
        <span className="mt-2 inline-block rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          {caseStudy.badge}
        </span>
      )}
      <h3 className="heading-bold mt-2.5 font-display text-[1.2rem] leading-tight text-foreground">
        {caseStudy.headline}
      </h3>
      {caseStudy.teaser && (
        <p className="mt-2 text-xs leading-6 text-muted-foreground">{caseStudy.teaser}</p>
      )}
      {caseStudy.metrics.length > 0 && (
        <div className="mt-3.5 flex flex-wrap gap-x-6 gap-y-2 border-t border-border/60 pt-3.5">
          {caseStudy.metrics.map((m) => (
            <div key={m.label}>
              <small className="block text-[0.62rem] text-muted-foreground">{m.label}</small>
              <strong className="font-display text-[1.2rem] leading-none tabular-nums text-foreground">
                {m.value}
              </strong>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        className="mt-4 w-full rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
      >
        Make your call →
      </button>
    </div>
  );
}

// ── Dialogs ──────────────────────────────────────────────────────────
function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <strong className="text-sm font-bold text-foreground">{title}</strong>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

function PathDialog({
  stages,
  here,
  onPick,
  onClose,
}: {
  stages: AcademyStageProgress[];
  here: AcademyStageProgress | null;
  onPick: (key: string) => void;
  onClose: () => void;
}) {
  return (
    <Dialog title="Your acquisition learning path" onClose={onClose}>
      <p className="text-xs leading-6 text-muted-foreground">
        Start where you are. You can revisit any stage whenever your deal calls for it.
      </p>
      <div className="mt-4 space-y-2">
        {stages.map((stage, i) => {
          const done = stage.lessonCount > 0 && stage.completedCount === stage.lessonCount;
          return (
            <div
              key={stage.key}
              className={cn(
                "flex items-center gap-3 rounded-xl border p-3",
                here?.key === stage.key ? "border-primary/50 bg-primary/10" : "border-border",
              )}
            >
              <span
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold",
                  done
                    ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-600"
                    : "border-border bg-muted/50 text-muted-foreground",
                )}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <strong className="block text-xs font-bold text-foreground">{stage.label}</strong>
                <small className="text-[0.68rem] text-muted-foreground">
                  {stage.lessonCount === 0
                    ? "No lessons yet"
                    : `${stage.completedCount} of ${stage.lessonCount} complete`}
                </small>
              </div>
              {stage.lessonCount > 0 && (
                <button
                  type="button"
                  onClick={() => onPick(stage.key)}
                  className="shrink-0 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
                >
                  Explore →
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

function CaseDialog({
  caseStudy,
  openableIds,
  onClose,
  onOpenLesson,
}: {
  caseStudy: NonNullable<AcademyOverview["case_study"]>;
  /** The link is hidden rather than dead when the lesson isn't reachable. */
  openableIds: Set<string>;
  onClose: () => void;
  onOpenLesson: (id: string) => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);

  return (
    <Dialog title={caseStudy.eyebrow} onClose={onClose}>
      {caseStudy.badge && (
        <span className="inline-block rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          {caseStudy.badge}
        </span>
      )}
      <h3 className="heading-bold mt-2.5 font-display text-[1.45rem] leading-tight text-foreground">
        {caseStudy.headline}
      </h3>
      {caseStudy.prompt && (
        <p className="mt-2.5 text-sm leading-7 text-muted-foreground">{caseStudy.prompt}</p>
      )}

      {caseStudy.metrics.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 rounded-xl border border-border bg-muted/30 p-3.5">
          {caseStudy.metrics.map((m) => (
            <div key={m.label}>
              <small className="block text-[0.62rem] text-muted-foreground">{m.label}</small>
              <strong className="font-display text-[1.2rem] leading-none tabular-nums text-foreground">
                {m.value}
              </strong>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 space-y-2">
        {caseStudy.options.map((option, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setPicked(i)}
            className={cn(
              "w-full rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors",
              picked === i
                ? "border-primary/50 bg-primary/10 text-foreground"
                : "border-border bg-background text-foreground hover:border-primary/40",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {picked !== null && (
        <div aria-live="polite" className="mt-4">
          <div className="rounded-xl border border-border bg-muted/30 p-3.5 text-xs leading-6 text-muted-foreground">
            {caseStudy.options[picked].feedback}
          </div>
          {caseStudy.lesson_id && openableIds.has(caseStudy.lesson_id) && (
            <button
              type="button"
              onClick={() => onOpenLesson(caseStudy.lesson_id!)}
              className="mt-3 text-xs font-semibold text-primary transition-opacity hover:opacity-80"
            >
              Explore {caseStudy.lesson_title ?? "the related lesson"} →
            </button>
          )}
        </div>
      )}
    </Dialog>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────
function SectionHead({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h2 className="heading-bold font-display text-[1.45rem] text-foreground">{title}</h2>
        {description && (
          <p className="mt-1 text-xs leading-6 text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

function TopicTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors",
        // Solid ink pill when selected, per the design. `bg-foreground` +
        // `text-background` rather than a literal colour so it inverts
        // correctly on the dark theme instead of going dark-on-dark.
        active
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
